import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CircleHelpIcon, PlayIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExerciseSession } from "@/components/exercise-session";
import { Button } from "@/components/ui/button";
import { ProgressBar } from "@/components/ui/progress-bar";
import { RecordButton } from "@/components/ui/record-button";
import { useT } from "@/glossary";
import { ApiProcessingError, useScoreRecording } from "@/lib/api";
import type { SyllableSegmentationData as SyllableSegmentation } from "@/lib/api-client";
import {
  stopAllSounds,
  usePlayBundledSounds,
} from "@/lib/audio/use-play-sound";
import { usePlaySuccessSoundIfEnabled } from "@/lib/audio/use-success-sound";
import { useSyllableTiming } from "@/lib/audio/use-syllable-timing";
import { cn } from "@/lib/cn";
import { RECORDING_START_MS } from "@/lib/constants";
import type { PROD } from "@/lib/data/words";
import { prodStore } from "@/lib/stores/prod";
import { useUserPrefs } from "@/lib/stores/user-prefs";
import { AttemptDisplay, type AttemptInfo } from "./-attempt-display";
import { CardButton } from "./-card-button";
import { ConsentPrompt } from "./-consent-prompt";
import { SkipPrompt } from "./-skip-prompt";
import { type CardFace, WordCard } from "./-word-card";

const MAX_MISTAKES = 3;
const HINTS_PLAY_DELAY_MS = 200;

export const Route = createFileRoute("/prod/exercise")({
  component: ProdExercise,
});

function ProdExercise() {
  return (
    <ExerciseSession id="prod">
      <ActiveExercise />
    </ExerciseSession>
  );
}

const ActiveExercise = () => {
  const prefs = useUserPrefs();
  const wordIndex = prodStore.useCurrentWordIndex();
  const view = prodStore.useCurrent();

  if (view.kind !== "SHOW_CURRENT_WORD") return null;

  if (prefs?.audioAuthorization === undefined) return <ConsentPrompt />;

  const { word, results, progress } = view.data;

  return (
    <ExerciseWord
      key={wordIndex}
      word={word}
      results={results}
      progress={progress}
    />
  );
};

const SKIP_THRESHOLD = 3;

const ExerciseWord = ({
  word,
  results,
  progress,
}: {
  word: (typeof PROD)[number];
  results: {
    attempts: {
      correct: boolean;
      hintVisible: boolean;
      recording_b64: string;
      apiResponse: {
        highest: number;
        syllable_segmentation?: SyllableSegmentation;
      };
    }[];
    resultConfirmed: boolean;
    score?: number;
  };
  progress: { current: number; total: number };
}) => {
  const navigate = useNavigate();
  const t = useT();
  const attempts = results.attempts;
  const hasAttempts = attempts.length > 0;
  const mistakeCount = attempts.filter((a) => !a.correct).length;
  const hasSuccess = attempts.some((a) => a.correct);
  const wordDone = hasSuccess || mistakeCount >= MAX_MISTAKES;
  const isLastWord = progress.current + 1 === progress.total;

  const [localRevealed, setLocalRevealed] = useState(false);
  const [cardFace, setCardFace] = useState<CardFace>("image");
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  // While a new attempt is being recorded/analyzed, hide the previous one so
  // the user is not distracted by old feedback.
  const [isRetrying, setIsRetrying] = useState(false);
  const showAttempts = hasAttempts && !isRetrying;
  const [playWord, isPlayingWord] = usePlayBundledSounds([
    word.audio.baseFileName,
  ]);
  const { activeSyllable, clearTiming, isPlaybackPending, playTimedWord } =
    useSyllableTiming(playWord, word.syllableTimings);
  const isPlaybackBusy = isPlayingWord || isPlaybackPending;
  const playSuccessSoundIfEnabled = usePlaySuccessSoundIfEnabled();
  const showSkipPrompt = consecutiveErrors >= SKIP_THRESHOLD;
  const hintsRevealed = localRevealed || attempts.some((a) => a.hintVisible);
  const showHintsNudge = mistakeCount >= 2 && !hintsRevealed;

  const lastAttempt = attempts[attempts.length - 1];

  useEffect(() => () => stopAllSounds(), []);

  const showHints = () => {
    setLocalRevealed(true);
    setCardFace("hints");
    playTimedWord(HINTS_PLAY_DELAY_MS);
  };

  const showImage = () => {
    clearTiming();
    setCardFace("image");
  };

  const scoring = useScoreRecording();

  const handleRecorded = (recording_b64: string) => {
    scoring.mutate(
      {
        recording_b64,
        word: {
          word: word.word,
          syllables: word.syllables,
          expected_stress_index: word.stressIndex,
        },
      },
      {
        onSuccess: (data) => {
          setIsRetrying(false);
          setConsecutiveErrors(0);
          const correct = data.highest === word.stressIndex;
          prodStore.addAttempt({
            recording_b64,
            apiResponse: {
              highest: data.highest,
              syllable_segmentation: data.syllable_segmentation,
            },
            correct,
            timestamp: Date.now(),
            hintVisible: hintsRevealed,
          });

          if (correct) playSuccessSoundIfEnabled();
        },
        onError: (error) => {
          setIsRetrying(false);
          setConsecutiveErrors((consecutiveErrors) => consecutiveErrors + 1);
          console.error(error);
          toast.error(
            error instanceof ApiProcessingError
              ? {
                  INVALID_AUDIO: t.errors.couldNotProcessAudio,
                  SEGMENTATION_FAILED: t.errors.couldNotAnalyzeSpeech,
                  INVALID_BASE64: t.errors.recordingFailed,
                }[error.code]
              : t.errors.somethingWentWrong,
          );
        },
      },
    );
  };

  const WordIcon = word.image;

  const attemptInfos: AttemptInfo[] = attempts.map((a) => ({
    predictedStressIndex: a.apiResponse.highest,
    correct: a.correct,
    recording_b64: a.recording_b64,
    syllableSegmentation: a.apiResponse.syllable_segmentation,
  }));
  const lastAttemptInfo = attemptInfos.at(-1);

  if (showSkipPrompt) {
    return (
      <SkipPrompt
        onKeepTrying={() => setConsecutiveErrors(0)}
        onSkip={() => {
          prodStore.skipRemaining();
          navigate({ to: "/prod/results" });
        }}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col px-4">
      <div className="flex min-h-0 flex-1 flex-col items-center gap-(--content-gap)">
        <ProgressBar
          current={progress.current + 1}
          total={progress.total}
          label={t.common.wordProgress(progress.current + 1, progress.total)}
        />

        <p className="text-muted-foreground">{t.prod.instruction}</p>

        <WordCard
          word={word}
          cardFace={cardFace}
          activeSyllable={activeSyllable}
        />

        <div className="grid w-full max-w-xs grid-cols-2 gap-4">
          <CardButton
            aria-label={t.prod.playWord}
            disabled={isPlaybackBusy}
            onClick={() =>
              cardFace === "hints" ? playTimedWord() : playWord()
            }
            className={cn(
              isPlaybackBusy &&
                "ring-2 ring-brand ring-offset-2 ring-offset-background",
              lastAttempt != null && !lastAttempt.correct && "replay-nudge",
            )}
          >
            <PlayIcon className="h-5 w-5 fill-current text-slate-600" />
            <span>{t.prod.playWordLabel}</span>
          </CardButton>

          {cardFace === "image" ? (
            <CardButton
              aria-label={t.prod.turnToHints}
              onClick={showHints}
              className={cn(showHintsNudge && "replay-nudge")}
            >
              <CircleHelpIcon className="h-5 w-5 text-slate-600" />
              <span>{t.prod.showHintsLabel}</span>
            </CardButton>
          ) : (
            <CardButton aria-label={t.prod.turnToImage} onClick={showImage}>
              {WordIcon ? (
                <WordIcon className="h-5 w-5 text-slate-600" />
              ) : (
                <CircleHelpIcon className="h-5 w-5 text-slate-600" />
              )}
              <span>{t.prod.hideHintsLabel}</span>
            </CardButton>
          )}
        </div>

        {/* Keep attempts block mounted to reserve layout space */}
        <div
          className={cn(
            "flex flex-col items-center gap-2",
            !showAttempts && "invisible opacity-0",
          )}
        >
          {lastAttemptInfo ? (
            <AttemptDisplay
              attempt={lastAttemptInfo}
              playLabel={t.prod.playMyAttempt}
              syllableCount={word.syllableCount}
            />
          ) : (
            <div className="h-53 w-64" aria-hidden="true" />
          )}
        </div>

        {showAttempts ? (
          <p aria-live="polite" className="h-7 font-semibold text-lg">
            {hasSuccess
              ? t.common.goodJob
              : mistakeCount >= MAX_MISTAKES
                ? t.prod.youllDoBetterNext
                : mistakeCount >= 2
                  ? t.prod.checkHintsAndTryAgain
                  : t.prod.listenToWordAndTryAgain}
          </p>
        ) : (
          <p aria-hidden="true" className="h-7" />
        )}
      </div>

      <div className="pt-4">
        {wordDone ? (
          <Button
            className="min-h-15 w-full rounded-full border-neutral-950 bg-white px-8 py-5 font-bold text-lg text-neutral-950 shadow-[0_12px_28px_rgba(0,0,0,0.12)] transition-all duration-200 hover:scale-[1.01] hover:bg-neutral-50 active:scale-[0.99]"
            onClick={() => {
              const { isLast } = prodStore.confirmResult();
              if (isLast) navigate({ to: "/prod/results" });
            }}
            priority
          >
            {isLastWord ? t.common.next : t.prod.nextWord}
          </Button>
        ) : (
          <RecordButton
            onRecorded={handleRecorded}
            onRecordingStart={() => setIsRetrying(hasAttempts)}
            recordingDurationMs={RECORDING_START_MS + 3 * word.audio.durationMs}
            idleLabel={mistakeCount > 0 ? t.prod.speakAgain : t.prod.speak}
            isPending={scoring.isPending}
          />
        )}
      </div>
    </div>
  );
};
